import HistoryRounded from "@mui/icons-material/HistoryRounded";
import RestoreRounded from "@mui/icons-material/RestoreRounded";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useId } from "react";
import { useTranslation } from "react-i18next";
import MetaChip from "../../../components/UIToolkit/Badges/MetaChip";
import { AccentIconTile } from "../../../components/UIToolkit/molecules/AccentIconTile";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { ALPHA, FONT_SIZE, INK, LINE_HEIGHT, MOTION } from "../../../theme/tokens";
import type { CustomPrintoutVersionSummary } from "./customPrintoutTypes";
import { useCustomPrintoutVersionMeta } from "./useCustomPrintoutVersionMeta";

type Props = {
	version: CustomPrintoutVersionSummary;
	/** The printout's current version number, which everyone else keeps printing meanwhile. */
	currentVersionNumber: number;
	/** Can restore (not read-only here). */
	canRestore: boolean;
	isRestoring: boolean;
	onRestore: () => void;
	onBackToCurrent: () => void;
};

/**
 * Over the preview while an earlier version is on screen: which version it is, who saved it when, what printing does
 * now, and the two ways out — Back to current, and (for editors) Restore this version. The draft bar's tint, so the
 * two "this isn't what's saved as current" states read as one family.
 */
export const CustomPrintoutVersionBar: FC<Props> = ({ version, currentVersionNumber, canRestore, isRestoring, onRestore, onBackToCurrent }) => {
	const { t } = useTranslation();
	const titleId = useId();
	const metaOf = useCustomPrintoutVersionMeta();
	return (
		<Box
			component="section"
			aria-labelledby={titleId}
			data-testid="custom-printout-version-bar"
			sx={(theme) => ({
				flexShrink: 0,
				display: "flex",
				alignItems: "center",
				flexWrap: "wrap",
				columnGap: 1.5,
				rowGap: 1,
				px: 1.5,
				py: 1.25,
				borderRadius: 1,
				border: `1px solid ${alpha(theme.palette.primary.main, ALPHA.FIELD_OUTLINE)}`,
				background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, ALPHA.BRAND_TINT)}, ${alpha(theme.palette.primary.main, ALPHA.HOVER_OVERLAY)})`,
				"@keyframes customPrintoutVersionBarIn": { from: { opacity: 0, transform: "translateY(-4px)" }, to: { opacity: 1, transform: "none" } },
				animation: `customPrintoutVersionBarIn ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT}`,
				"@media (prefers-reduced-motion: reduce)": { animation: "none" },
			})}
		>
			<AccentIconTile size="sm">
				<HistoryRounded />
			</AccentIconTile>
			<Box sx={{ flex: "1 1 240px", minWidth: 0 }}>
				<Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
					<Typography id={titleId} component="h3" sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, lineHeight: LINE_HEIGHT.TIGHT, color: "primary.dark" }}>
						{t("printout.custom.ai.version.viewing", { number: version.versionNumber })}
					</Typography>
					{version.restoredFromVersionNumber !== null && (
						<MetaChip
							icon={<RestoreRounded />}
							tone="primary"
							label={t("printout.custom.ai.version.restored.from", { number: version.restoredFromVersionNumber })}
							dataTestId="custom-printout-version-bar-restored-from"
						/>
					)}
				</Box>
				<Typography
					data-testid="custom-printout-version-bar-meta"
					sx={{ mt: 0.25, fontSize: FONT_SIZE.CAPTION, lineHeight: LINE_HEIGHT.SNUG, color: INK.SECONDARY }}
				>
					{metaOf(version)}
				</Typography>
				{/* Not when the panes stack: there the preview under the bar is already short, and the title, the ring
				    around the page and the two buttons carry the state. */}
				<Typography sx={{ display: { xs: "none", md: "block" }, mt: 0.25, fontSize: FONT_SIZE.CAPTION, lineHeight: LINE_HEIGHT.SNUG, color: INK.MUTED }}>
					{t("printout.custom.ai.version.viewing.hint", { current: currentVersionNumber })}
				</Typography>
			</Box>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, marginInlineStart: "auto" }}>
				<SystemButton
					data-testid="custom-printout-version-back-button"
					buttonVariant="secondary"
					size="small"
					noFullWidthMobile
					onClick={onBackToCurrent}
					disabled={isRestoring}
				>
					{t("printout.custom.ai.version.back")}
				</SystemButton>
				{canRestore && (
					<SystemButton
						data-testid="custom-printout-version-restore-button"
						buttonVariant="primary"
						size="small"
						noFullWidthMobile
						leadingIcon={<RestoreRounded />}
						onClick={onRestore}
						loading={isRestoring}
						disabled={isRestoring}
					>
						{t("printout.custom.ai.version.restore")}
					</SystemButton>
				)}
			</Box>
		</Box>
	);
};
